/**
 * Legacy / wrong storefront category URLs → current category or brand paths (301).
 * Source: SEO-NEW-test Excel product-category rows + known WP renames.
 * Keep in sync with Server: utils/legacyStorefrontRedirects.js
 */

const SMARTPHONES = "/product-category/phones-tablets/smartphones/";
const FEATURE_PHONES = "/product-category/phones-tablets/feature-phones/";
const TABLETS = "/product-category/phones-tablets/tablets/";
const PHONES_TABLETS = "/product-category/phones-tablets/";
const WEARABLES = "/product-category/wearables/";
const AUDIO = "/product-category/accessories/audio/";
const SPEAKERS = "/product-category/accessories/audio/speakers/";
const CASES = "/product-category/accessories/phone-accessories/cases-covers/";
const CHARGERS = "/product-category/accessories/phone-accessories/chargers/";
const POWERBANKS = "/product-category/accessories/phone-accessories/powerbanks/";
const EARBUDS = "/product-category/accessories/phone-accessories/earbuds-pods/";
const MEMORY = "/product-category/accessories/memory-storage/";
const PHONE_ACCESSORIES = "/product-category/accessories/phone-accessories/";
const ACCESSORIES = "/product-category/accessories/";
const HOME_ENT = "/product-category/home-entertainment/";
const TVS = "/product-category/home-entertainment/televisions/";
const WIFI = "/product-category/home-entertainment/wifi-routers/";
const ALL_PRODUCTS = "/product-category/all-products/";
const OFFERS = "/offers/";

/** Known brand slugs (exact /product-tag/{slug} → brand listing). */
const BRAND_SLUGS = new Set([
  "airtel",
  "alcatel",
  "amaya",
  "apple",
  "asus",
  "belkin",
  "bontel",
  "calus",
  "faiba",
  "fitbit",
  "hmd",
  "hp",
  "huawei",
  "infinix",
  "itel",
  "jbl",
  "jsb",
  "kabambe",
  "lexar",
  "mi",
  "nokia",
  "nothing",
  "oppo",
  "oraimo",
  "punex",
  "pzx",
  "realme",
  "redmi",
  "samsung",
  "sandisk",
  "scooper",
  "sony",
  "tcl",
  "tecno",
  "tp-link",
  "western-digital",
  "wiko",
  "x-cell",
  "xiaomi",
]);

function brandPath(slug) {
  return `${ALL_PRODUCTS}${slug}/`;
}

function withSlashVariants(map) {
  const out = { ...map };
  for (const [from, to] of Object.entries(map)) {
    if (from.endsWith("/")) {
      const noSlash = from.slice(0, -1);
      if (!out[noSlash]) out[noSlash] = to;
    } else if (!out[`${from}/`]) {
      out[`${from}/`] = to;
    }
  }
  return out;
}

/**
 * Exact path redirects from SEO Excel (404s + wrong brand/price/sale paths).
 * Real categories (smartphones, tablets, accessories, …) are intentionally omitted.
 */
const EXACT = withSlashVariants({
  "/privacy-policy": "/privacy-policy-2/",
  "/privacy-policy/": "/privacy-policy-2/",

  // —— Legacy WP blog posts (root-level permalinks) → /blog/{slug} ——
  // WP's own post_name for this one was "introducing-samsung-accidental-screen-damage-cover";
  // the site already had this post under a different slug before the import, so the redirect
  // target intentionally differs from the legacy slug.
  "/introducing-samsung-accidental-screen-damage-cover/":
    "/blog/introducing-1-year-samsung-accidental-screen-damage-cover/",
  // WP also has a separate static Page (not the blog post above) at this
  // near-identical slug — same destination, both indexed.
  "/introducing-1-year-samsung-accidental-screen-damage-cover/":
    "/blog/introducing-1-year-samsung-accidental-screen-damage-cover/",
  "/how-to-protect-your-phone-and-its-data/": "/blog/how-to-protect-your-phone-and-its-data/",
  "/faiba/": "/blog/faiba/",
  "/tips-for-taking-care-of-your-phone/": "/blog/tips-for-taking-care-of-your-phone/",
  "/samsung-galaxy-s23-and-s23-ultra-features-and-price-in-kenya/":
    "/blog/samsung-galaxy-s23-and-s23-ultra-features-and-price-in-kenya/",
  "/samsung-unpacked-2023/": "/blog/samsung-unpacked-2023/",
  "/tips-on-setting-and-safeguarding-your-password/":
    "/blog/tips-on-setting-and-safeguarding-your-password/",

  // —— Other legacy root pages ——
  "/our-branch-locations/": "/store-locator/",
  // WooCommerce's default shop archive — no equivalent page, closest match.
  "/shop/": ALL_PRODUCTS,
  // Live, real marketing content on WP (verified — not dead), no dedicated
  // "ex-display" listing on the new site yet, so route to the brand.
  "/ex-display/": brandPath("samsung"),
  "/samsung-clearance-sale/": OFFERS,
  // Duplicate/older version of the real FAQ page.
  "/faqv1/": "/faq/",

  // —— Price bands → smartphones + maxPrice ——
  "/product-category/phones-tablets/smartphones/under-10k-phones/":
    `${SMARTPHONES}?maxPrice=10000`,
  "/product-category/phones-tablets/smartphones/under-15k-smartphones/":
    `${SMARTPHONES}?maxPrice=15000`,
  "/product-category/under-10k-phones/": `${SMARTPHONES}?maxPrice=10000`,
  "/product-category/under-15k-smartphones/": `${SMARTPHONES}?maxPrice=15000`,
  "/product-category/phones-under-20000/": `${SMARTPHONES}?maxPrice=20000`,
  "/product-category/phones-under-25000/": `${SMARTPHONES}?maxPrice=25000`,
  "/product-category/phones-under-30000/": `${SMARTPHONES}?maxPrice=30000`,

  // —— Renamed / moved category slugs ——
  "/product-category/accessories/cases-and-covers/": CASES,
  "/product-category/accessories/phone-accessories/cases-and-covers/": CASES,
  "/product-category/accessories/phone-accessories/ear-buds/": EARBUDS,
  "/product-category/accessories/wifi-routers/": WIFI,
  "/product-category/accessories/computer-accessories/external-hard-disks/":
    MEMORY,
  "/product-category/accessories/original-accessories/": PHONE_ACCESSORIES,
  "/product-category/wearables/smartwatches-wearables/": WEARABLES,
  "/product-category/home-entertainment/speakers/": SPEAKERS,
  "/product-category/electronics/tvs-electronics/": HOME_ENT,
  "/product-category/electronics/televisions/": TVS,
  "/product-category/phones-tablets/phones-tablets-phones-tablets/":
    PHONES_TABLETS,

  // —— Brand mistaken as category / alias brand slugs ——
  "/product-category/itel/": brandPath("itel"),
  // Redmi is Xiaomi's sub-brand; catalog tags Redmi products under the
  // Xiaomi brand, not a separate "redmi" brand — WP treated it as distinct.
  "/product-category/redmi/": brandPath("xiaomi"),
  "/product-category/all-products/redmi/": brandPath("xiaomi"),
  "/product-category/nothing-phones/": brandPath("nothing"),
  "/product-category/phones-tablets/smartphones/nothing-phones-smartphones/":
    brandPath("nothing"),
  "/product-category/laptops/asus/": brandPath("asus"),
  "/product-category/all-products/hmd-phones-in-kenya/": brandPath("hmd"),
  "/product-category/all-products/samsung-ex-diplay-phones/":
    brandPath("samsung"),
  "/product-category/samsung-premium/": brandPath("samsung"),
  "/product-category/samsung-2023-models-clearance-sale/": brandPath("samsung"),
  "/product-category/samsung_accessories/": PHONE_ACCESSORIES,

  // —— Sale / deal pseudo-categories → offers ——
  "/product-category/accessories-sale/": OFFERS,
  "/product-category/anniversary-deals/": OFFERS,
  "/product-category/clearance-sale/": OFFERS,
  "/product-category/deals/": OFFERS,
});

function resolveProductTag(slug) {
  const s = decodeURIComponent(slug || "")
    .trim()
    .toLowerCase()
    .replace(/\/+$/, "");
  if (!s) return SMARTPHONES;

  // Redmi is Xiaomi's sub-brand in this catalog, not its own Brand doc.
  if (s === "redmi") return brandPath("xiaomi");

  if (BRAND_SLUGS.has(s)) return brandPath(s);

  for (const brand of BRAND_SLUGS) {
    if (
      s === `${brand}-kenya` ||
      s === `${brand}-phones` ||
      s === `${brand}-smartphones` ||
      s.startsWith(`${brand}-phone`) ||
      (/^(affordable|budget|best|cheap)-/.test(s) &&
        (s.includes(`-${brand}-`) ||
          s.includes(`-${brand}`) ||
          s.startsWith(`${brand}-`)))
    ) {
      return brandPath(brand);
    }
  }

  if (
    /under-?10k|under-?10000|entry-level|budget-phone|budgetphone|affordable-(android|mobile|smartphone)/i.test(
      s,
    )
  ) {
    return `${SMARTPHONES}?maxPrice=15000`;
  }

  if (/feature-?phone|basic-?phone|2gphone|torch-?phone/i.test(s)) {
    return FEATURE_PHONES;
  }

  if (/tablet/i.test(s)) return TABLETS;

  if (/smart-?watch|wearable|bluetooth-calling-watch/i.test(s)) {
    return WEARABLES;
  }

  if (
    /earbud|earphone|headphone|anc-|true-wireless|bluetooth-ear|wireless-ear|type-c-ear/i.test(
      s,
    )
  ) {
    return AUDIO;
  }

  if (/case|cover|anti-yellow|anti-slip|transparent-case/i.test(s)) {
    return CASES;
  }

  if (/power-?bank/i.test(s)) return POWERBANKS;
  if (/charger|wireless-charging/i.test(s)) return CHARGERS;

  if (
    /flash-?disk|memory-?card|sd-?card|pendrive|hard-?disk|ssd|storage-device/i.test(
      s,
    )
  ) {
    return MEMORY;
  }

  if (/phone-?accessor|audio-accessor/i.test(s)) return PHONE_ACCESSORIES;
  if (/accessor/i.test(s)) return ACCESSORIES;

  if (/tv|television|wifi-?router|mifi|router/i.test(s)) return HOME_ENT;

  return SMARTPHONES;
}

/**
 * @param {string} pathname
 * @returns {string|null}
 */
export function resolveLegacyStorefrontRedirect(pathname) {
  const p = (pathname || "/").split("?")[0] || "/";
  if (EXACT[p]) return EXACT[p];

  let m = p.match(/^\/product-category\/phones-under-(\d+)\/?$/i);
  if (m) return `${SMARTPHONES}?maxPrice=${m[1]}`;

  m = p.match(
    /^\/product-category\/phones-tablets\/smartphones\/(under-10k-phones|under-15k-smartphones)\/page\/\d+\/?$/i,
  );
  if (m) {
    return m[1].includes("10k")
      ? `${SMARTPHONES}?maxPrice=10000`
      : `${SMARTPHONES}?maxPrice=15000`;
  }

  m = p.match(/^\/product-tag\/([^/]+)\/?(?:page\/\d+\/?)?$/i);
  if (m) return resolveProductTag(m[1]);

  return null;
}

export default resolveLegacyStorefrontRedirect;
